import type { FundDetail } from "@/data/fund-detail-types";
import { useLiveHoldings } from "@/hooks/use-etf-live-holdings";
import { downloadCsv, rowsToCsv } from "@/lib/csv-download";
import { cn } from "@/lib/utils";
import { useCallback } from "react";

type Instrument = {
  name: string;
  desc: string;
  badge: string;
  badgeType: "options" | "call" | "put" | "tbill" | "cash";
  weight: string;
  marketValue?: string;
};

const CASH_ROW_LABEL = "Cash & Cash Equivalents (Incl. U.S. Treasuries)";

function formatTotalUsd(val: number): string {
  return `$${Math.round(val).toLocaleString("en-US")}`;
}

export function BufferHoldings({ fund }: { fund: FundDetail }) {
  const refTicker = fund.referenceTicker ?? "REF";
  const { holdings: liveHoldings, dateLabel: liveDateLabel, isLive, aum } = useLiveHoldings(fund);

  const dateLabel = isLive ? liveDateLabel : (fund.holdingsAsOf ?? fund.dateLabel);
  const isDetailed = isLive && liveHoldings.length > 0;

  const rows: Instrument[] = isDetailed
    ? liveHoldings.map((h) => {
        if (h.sector === "Cash") {
          return {
            name: CASH_ROW_LABEL,
            desc: "",
            badge: "CASH",
            badgeType: "cash" as const,
            weight: h.pct == null ? "—" : `${h.pct.toFixed(2)}%`,
            marketValue: h.marketValue,
          };
        }
        const isCall = h.name.trim().endsWith(" C");
        const direction = (h.pct ?? 0) >= 0 ? "Purchased" : "Written";
        return {
          name: h.name,
          desc: `${direction} FLEX ${isCall ? "call" : "put"}`,
          badge: isCall ? "CALL" : "PUT",
          badgeType: (isCall ? "call" : "put") as "call" | "put",
          weight: h.pct == null ? "—" : `${h.pct.toFixed(2)}%`,
          marketValue: h.marketValue,
        };
      })
    : [];

  const handleDownload = useCallback(() => {
    if (!isLive || liveHoldings.length === 0) return;
    const slug = fund.ticker.toLowerCase();
    const dateForFile = (dateLabel || "").replace(/[^A-Za-z0-9]+/g, "-");
    const filename = `${slug}-holdings${dateForFile ? `-${dateForFile}` : ""}.csv`;
    const csv = rowsToCsv(liveHoldings, [
      { header: "Name", value: (h) => h.name },
      { header: "Ticker", value: (h) => h.ticker },
      { header: "Weight (%)", value: (h) => (h.pct == null ? "" : h.pct.toFixed(2)) },
      { header: "Market Value", value: (h) => h.marketValue ?? "" },
    ]);
    downloadCsv(filename, csv);
  }, [fund.ticker, dateLabel, isLive, liveHoldings]);

  const gridCols = "grid-cols-[1fr_80px_100px_80px] max-[480px]:grid-cols-[1fr_64px]";

  const showTable = isDetailed;

  const liveOptionLegCount = liveHoldings.filter((h) => h.sector === "Options").length;
  const expectedLegs = (fund.bufferLevel ?? 0) >= 100 ? 3 : 4;
  const isPartialData = isDetailed && liveOptionLegCount < expectedLegs;

  return (
    <section
      id="sec-holdings"
      data-fund-section="bufferConfig hasLiveHoldings"
      className="border-b border-[var(--etf-border)]"
    >
      <div className="flex items-center justify-between px-10 max-md:px-4 py-6 border-b border-[var(--etf-border)] gap-3 max-md:flex-col max-md:items-start max-md:gap-2">
        <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
          <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
          Holdings
        </h2>
        <p className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em] max-md:pl-[15px]">
          FLEX Options &middot; As of {dateLabel} &middot; Subject to change
        </p>
      </div>

      {isPartialData && (
        <div
          role="status"
          aria-live="polite"
          className="mx-10 max-md:mx-4 max-[480px]:mx-3 mt-4 mb-2 px-4 py-3 border border-[rgba(255,193,7,0.32)] bg-[rgba(255,193,7,0.06)]"
        >
          <p className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white2)] leading-[1.55] m-0">
            <span className="text-[#ffc107] font-semibold tracking-[0.02em]">
              Holdings ingestion in progress
            </span>{" "}
            — some position legs may not yet be reflected. Last updated {dateLabel}.
          </p>
        </div>
      )}

      {showTable ? (
        <>
          <div
            className={`grid ${gridCols} gap-3 px-10 max-md:px-4 max-[480px]:px-3 py-2 border-b border-[var(--etf-border2)] bg-[var(--etf-black)]`}
          >
            <div className="font-[family-name:var(--font-geist)] text-[11px] tracking-[0.12em] text-[var(--etf-white3)] uppercase">
              Instrument
            </div>
            <div className="font-[family-name:var(--font-geist)] text-[11px] tracking-[0.12em] text-[var(--etf-white3)] uppercase text-right">
              Type
            </div>
            <div className="font-[family-name:var(--font-geist)] text-[11px] tracking-[0.12em] text-[var(--etf-white3)] uppercase text-right max-[480px]:hidden">
              Mkt Value
            </div>
            <div className="font-[family-name:var(--font-geist)] text-[11px] tracking-[0.12em] text-[var(--etf-white3)] uppercase text-right">
              Weight
            </div>
          </div>

          {rows.map((inst, i) => (
            <div
              key={inst.name}
              className={`grid ${gridCols} gap-3 items-center px-10 max-md:px-4 max-[480px]:px-3 py-3 border-b border-[var(--etf-border2)] transition-[background] duration-[120ms] hover:bg-white/[0.02]`}
              style={i === 0 ? { background: "rgba(255,85,0,0.03)" } : undefined}
            >
              <div>
                <div
                  className="font-[family-name:var(--font-geist)] text-[13px] font-semibold text-[var(--etf-white)] tracking-[0.06em] mb-[3px]"
                  style={i === 0 ? { color: "var(--etf-orange)" } : undefined}
                >
                  {inst.name}
                </div>
                {inst.desc && (
                  <div className="text-[11px] text-[var(--etf-white3)]">{inst.desc}</div>
                )}
              </div>
              <div className="text-right max-[480px]:hidden">
                <span
                  className={cn(
                    "font-[family-name:var(--font-geist)] text-[11px] rounded-full inline-flex items-center justify-center w-[68px] box-border",
                    "px-3 py-0.5",
                    inst.badgeType === "options" &&
                      "bg-[rgba(255,85,0,0.12)] text-[var(--etf-orange)]",
                    inst.badgeType === "call" && "bg-[rgba(0,200,120,0.12)] text-[#00c878]",
                    inst.badgeType === "put" && "bg-[rgba(74,128,212,0.12)] text-[#4a80d4]",
                    inst.badgeType === "tbill" && "bg-[rgba(0,178,158,0.12)] text-[#00b29e]",
                    inst.badgeType === "cash" && "bg-white/[0.07] text-[rgba(255,255,255,0.5)]",
                  )}
                >
                  {inst.badge}
                </span>
              </div>
              <div className="font-[family-name:var(--font-geist)] text-[13px] text-[var(--etf-white2)] text-right max-[480px]:hidden">
                {inst.marketValue ?? "—"}
              </div>
              <div className="font-[family-name:var(--font-geist)] text-[13px] font-medium text-[var(--etf-white)] text-right">
                {inst.weight}
              </div>
            </div>
          ))}

          <div
            className={`grid ${gridCols} gap-3 items-center px-10 max-md:px-4 max-[480px]:px-3 py-3 border-t border-[var(--etf-border)] bg-[var(--etf-black)]`}
          >
            <div className="font-[family-name:var(--font-geist)] text-[13px] font-bold text-[var(--etf-white)] tracking-[0.06em]">
              TOTAL
            </div>
            <div />
            <div className="font-[family-name:var(--font-geist)] text-[13px] font-bold text-[var(--etf-white)] text-right max-[480px]:hidden">
              {aum != null ? formatTotalUsd(aum) : "—"}
            </div>
            <div className="font-[family-name:var(--font-geist)] text-[13px] font-bold text-[var(--etf-white)] text-right">
              100.00%
            </div>
          </div>
        </>
      ) : (
        <div className="px-10 max-md:px-4 py-10 text-center">
          <p className="font-[family-name:var(--font-instrument)] text-[12px] italic text-[var(--etf-white3)] m-0">
            Holdings data is not yet available.
          </p>
        </div>
      )}

      <div className="px-10 max-md:px-4 max-[480px]:px-3 py-4 font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-white2)] flex items-center justify-between border-t border-[var(--etf-border)] gap-3 max-md:flex-col max-md:items-start max-md:gap-3">
        <span className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] max-md:text-[12px] max-md:leading-[1.5]">
          FLEX Options-based&nbsp;&middot;&nbsp;No direct stock
          holdings&nbsp;&middot;&nbsp;Underlying: {refTicker}
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-[5px] px-[11px] py-[5px] pr-[13px] border-[1.5px] border-[var(--etf-orange)] rounded-full text-[var(--etf-orange)] font-[family-name:var(--font-instrument)] text-[11px] cursor-pointer bg-transparent relative overflow-hidden whitespace-nowrap tracking-[0.04em] uppercase transition-colors duration-[180ms] hover:bg-[rgba(255,85,0,0.06)] disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleDownload}
            disabled={!showTable}
            aria-label="Download holdings data as CSV"
          >
            <span className="relative z-[1] text-[13px] leading-none" aria-hidden="true">
              &#8595;
            </span>
            <span className="relative z-[1]">Download Holdings</span>
          </button>
        </div>
      </div>
    </section>
  );
}
